import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminApiClient } from "../../lib/adminApiClient";
import { queryKeys } from "../../lib/queryClient";
import { ErrorBanner, LoadingState, describeError } from "../../components/States";

export function AdminPostEditPage() {
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: post, isLoading, isError, error, refetch } = useQuery({
    queryKey: queryKeys.adminPost(id ?? ""),
    queryFn: () => adminApiClient.getPost(id as string),
    enabled: isEditMode,
  });

  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (post) {
      setTitle(post.title);
      setSlug(post.slug);
      setExcerpt(post.excerpt ?? "");
      setBodyHtml(post.bodyHtml);
    }
  }, [post]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.adminPosts });
    if (id) void queryClient.invalidateQueries({ queryKey: queryKeys.adminPost(id) });
  };

  const createPost = useMutation({
    mutationFn: () =>
      adminApiClient.createPost({
        title: title.trim(),
        excerpt: excerpt.trim() || undefined,
        bodyHtml,
      }),
    onSuccess: (created) => {
      invalidate();
      navigate(`/admin/posts/${created.id}`, { replace: true });
    },
  });

  const updatePost = useMutation({
    mutationFn: () =>
      adminApiClient.updatePost(id as string, {
        title: title.trim(),
        slug: slug.trim() || undefined,
        excerpt: excerpt.trim() || undefined,
        bodyHtml,
      }),
    onSuccess: invalidate,
  });

  const publishPost = useMutation({
    mutationFn: () => adminApiClient.publishPost(id as string),
    onSuccess: invalidate,
  });

  const unpublishPost = useMutation({
    mutationFn: () => adminApiClient.unpublishPost(id as string),
    onSuccess: invalidate,
  });

  const deletePost = useMutation({
    mutationFn: () => adminApiClient.deletePost(id as string),
    onSuccess: () => {
      invalidate();
      navigate("/admin/posts", { replace: true });
    },
  });

  if (isEditMode && isLoading) return <LoadingState label="Loading post…" />;
  if (isEditMode && isError) return <ErrorBanner error={error} onRetry={() => void refetch()} />;

  const saving = createPost.isPending || updatePost.isPending;
  const saveError = createPost.error ?? updatePost.error;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (title.trim().length < 3) {
      setFormError("Title must be at least 3 characters.");
      return;
    }
    if (bodyHtml.trim().length < 1) {
      setFormError("Post body can't be empty.");
      return;
    }
    if (isEditMode) updatePost.mutate();
    else createPost.mutate();
  }

  return (
    <div className="stack" style={{ maxWidth: 800 }}>
      <div className="row row--between">
        <h1>{isEditMode ? "Edit post" : "New post"}</h1>
        {post && (
          <span className={post.status === "PUBLISHED" ? "badge badge--success" : "badge"}>
            {post.status}
          </span>
        )}
      </div>

      <form className="card stack" onSubmit={handleSubmit}>
        <div className="field">
          <label htmlFor="post-title">Title</label>
          <input
            id="post-title"
            type="text"
            required
            minLength={3}
            maxLength={200}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        {isEditMode && (
          <div className="field">
            <label htmlFor="post-slug">URL slug</label>
            <input
              id="post-slug"
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
            />
            <span className="hint">marketonbase.com/blog/{slug || "…"}</span>
          </div>
        )}

        <div className="field">
          <label htmlFor="post-excerpt">Excerpt (optional)</label>
          <textarea
            id="post-excerpt"
            rows={2}
            maxLength={400}
            value={excerpt}
            onChange={(e) => setExcerpt(e.target.value)}
            placeholder="Shown on the blog list page and in social link previews."
          />
        </div>

        <div className="field">
          <label htmlFor="post-body">Body (HTML)</label>
          <textarea
            id="post-body"
            rows={20}
            required
            value={bodyHtml}
            onChange={(e) => setBodyHtml(e.target.value)}
            style={{ fontFamily: "var(--font-mono)", fontSize: "0.85rem" }}
            placeholder="<p>Write the post body as raw HTML — headings, paragraphs, links, images.</p>"
          />
          <span className="hint">
            Raw HTML, rendered as-is on the public post page — no Markdown conversion.
          </span>
        </div>

        {(formError || saveError) && (
          <p className="field-error-text" role="alert">
            {formError ?? describeError(saveError)}
          </p>
        )}

        <div className="row">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? "Saving…" : isEditMode ? "Save changes" : "Create draft"}
          </button>
        </div>
      </form>

      {isEditMode && post && (
        <div className="card stack">
          <h2>Publish</h2>
          <p className="text-sm muted">
            {post.status === "PUBLISHED"
              ? "Live on the public blog. Unpublishing takes it down immediately without deleting it."
              : "Not visible on the public blog yet."}
          </p>
          <div className="row">
            {post.status === "DRAFT" ? (
              <button
                type="button"
                className="btn btn-primary"
                disabled={publishPost.isPending}
                onClick={() => publishPost.mutate()}
              >
                Publish
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-secondary"
                disabled={unpublishPost.isPending}
                onClick={() => unpublishPost.mutate()}
              >
                Unpublish
              </button>
            )}
            <button
              type="button"
              className="btn btn-danger"
              disabled={deletePost.isPending}
              onClick={() => {
                if (window.confirm("Delete this post permanently? This can't be undone.")) {
                  deletePost.mutate();
                }
              }}
            >
              Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
